import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listPurchaseOrders, listPurchaseRequests } from '../../api/purchasing';
import { listStockBalances } from '../../api/inventory';
import { listItems } from '../../api/masterData';
import { listAttendanceEventsToday, listLeaveRequests } from '../../api/hr';
import { describeActivity, listAuditEvents, type AuditEventDetail } from '../../api/activity';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import { PrDetailDrawer } from '../../components/PrDetailDrawer';
import { ClickableRow, DashboardSkeleton, EmptyState, ErrorNote, KPICard, SectionCard, StatusBadge, formatDateTime, tableStyle, tdStyle, thStyle } from '../../components/common';
import { IconAlertTriangle, IconCalendar, IconClipboardCheck, IconFileText, IconShield } from '../../components/icons';
import type { AttendanceEvent, Item, LeaveRequest, PurchaseOrder, PurchaseRequest, StockBalance } from '../../types/domain';

/**
 * Manager dashboard — REAL DATA ONLY. Previously mixed real Purchasing/
 * Inventory KPIs with illustrative Sales/Target/Attendance-% figures; the
 * owner rejected keeping those merely because they resembled the
 * reference screenshot's composition. D16 (sales/target authoritative
 * source) remains unresolved, so those KPIs are removed outright rather
 * than kept as fake numbers — the freed space now holds real attendance
 * (attendance_event.read is granted to MANAGER per migration 0036),
 * real leave queue, and real recent activity.
 */
export default function ManagerDashboard() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { activeBranchId, activeBranchWarehouseIds } = useBranch();
  const [prs, setPrs] = useState<PurchaseRequest[] | null>(null);
  const [pos, setPos] = useState<PurchaseOrder[] | null>(null);
  const [balances, setBalances] = useState<StockBalance[] | null>(null);
  const [attendanceToday, setAttendanceToday] = useState<AttendanceEvent[] | null>(null);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[] | null>(null);
  const [events, setEvents] = useState<AuditEventDetail[] | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPr, setSelectedPr] = useState<PurchaseRequest | null>(null);

  const canReadPr = hasPermission('purchase_request', 'read');
  const canReadPo = hasPermission('purchase_order', 'read');
  const canReadBalance = hasPermission('stock_balance', 'read');
  const canReadAttendance = hasPermission('attendance_event', 'read');
  const canReadLeave = hasPermission('leave_request', 'read');
  const canReadItem = hasPermission('item', 'read');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      canReadPr ? listPurchaseRequests(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadPo ? listPurchaseOrders(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadBalance ? listStockBalances(undefined, activeBranchId ? activeBranchWarehouseIds : undefined) : Promise.resolve(null),
      canReadAttendance ? listAttendanceEventsToday(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadLeave ? listLeaveRequests(activeBranchId ?? undefined) : Promise.resolve(null),
      listAuditEvents(10),
      canReadItem ? listItems() : Promise.resolve([]),
    ])
      .then(([prRows, poRows, balanceRows, att, leave, ev, itemRows]) => {
        if (cancelled) return;
        setPrs(prRows);
        setPos(poRows);
        setBalances(balanceRows);
        setAttendanceToday(att);
        setLeaveRequests(leave);
        setEvents(ev);
        setItems(itemRows);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReadPr, canReadPo, canReadBalance, canReadAttendance, canReadLeave, canReadItem, activeBranchId, activeBranchWarehouseIds.join(',')]);

  if (loading) return <DashboardSkeleton />;
  if (error) return <ErrorNote text={error} />;

  const pendingPr = prs ? prs.filter((pr) => pr.status === 'SUBMITTED') : [];
  const openPo = pos ? pos.filter((po) => po.status === 'ISSUED' || po.status === 'PARTIALLY_RECEIVED').length : null;
  const lowStock = balances ? balances.filter((b) => b.qty_on_hand <= 5).length : null;
  const hadirCount = attendanceToday ? new Set(attendanceToday.filter((a) => a.event_type === 'CLOCK_IN').map((a) => a.employee_id)).size : null;
  const pendingLeave = leaveRequests ? leaveRequests.filter((l) => l.status === 'REQUESTED') : [];
  const itemLabel = (id: string) => items.find((it) => it.item_id === id)?.item_code ?? id.slice(0, 8);

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--alex-card-gap)', marginBottom: 'var(--alex-card-gap)' }}>
        {canReadPr && <KPICard label="PR menunggu approval" value={pendingPr.length} icon={<IconFileText size={16} />} tone="warning" linkTo="/app/purchase-requests" />}
        {canReadPo && <KPICard label="PO berjalan" value={openPo ?? '—'} icon={<IconClipboardCheck size={16} />} tone="primary" linkTo="/app/purchase-orders" />}
        {canReadBalance && <KPICard label="Item stok rendah" value={lowStock ?? '—'} icon={<IconAlertTriangle size={16} />} tone="danger" linkTo="/app/stock-balance" />}
        {canReadAttendance && <KPICard label="Hadir Hari Ini" value={hadirCount ?? 0} icon={<IconShield size={16} />} tone="success" />}
        {canReadLeave && <KPICard label="Cuti Pending" value={pendingLeave.length} icon={<IconCalendar size={16} />} tone="info" />}
      </div>

      <div className="alex-grid-8-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--alex-card-gap)' }}>
          {canReadPr && (
            <SectionCard title="Purchase Requests Menunggu Approval" action="/app/purchase-requests">
              {pendingPr.length === 0 ? (
                <EmptyState text="Tidak ada PR menunggu approval." variant="compact" />
              ) : (
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>No. PR</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Kebutuhan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingPr.slice(0, 6).map((pr) => (
                      <ClickableRow key={pr.pr_id} onClick={() => setSelectedPr(pr)}>
                        <td style={tdStyle}>{pr.pr_number}</td>
                        <td style={tdStyle}>
                          <StatusBadge status={pr.status} />
                        </td>
                        <td style={tdStyle}>{pr.needed_date ?? '—'}</td>
                      </ClickableRow>
                    ))}
                  </tbody>
                </table>
              )}
            </SectionCard>
          )}

          <SectionCard title="Aktivitas Cabang Terbaru">
            {!events || events.length === 0 ? (
              <EmptyState text="Belum ada aktivitas tercatat." variant="table" />
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Waktu</th>
                    <th style={thStyle}>Kejadian</th>
                    <th style={thStyle}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {events.slice(0, 8).map((e) => (
                    <tr key={e.event_id}>
                      <td style={tdStyle}>{formatDateTime(e.occurred_at)}</td>
                      <td style={tdStyle}>{describeActivity(e)}</td>
                      <td style={tdStyle}>
                        <StatusBadge status={e.action} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </SectionCard>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--alex-card-gap)' }}>
          {canReadLeave && (
            <SectionCard title="Cuti Pending">
              {pendingLeave.length === 0 ? (
                <EmptyState text="Tidak ada cuti pending." variant="compact" />
              ) : (
                <table style={tableStyle}>
                  <tbody>
                    {pendingLeave.slice(0, 5).map((l) => (
                      <tr key={l.leave_id}>
                        <td style={tdStyle}>{l.leave_type}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <StatusBadge status={l.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </SectionCard>
          )}

          {canReadBalance && balances && (
            <SectionCard title="Perlu Perhatian — Stok Habis">
              {balances.filter((b) => b.qty_on_hand <= 0).length === 0 ? (
                <EmptyState text="Tidak ada SKU dengan stok habis." variant="compact" />
              ) : (
                <table style={tableStyle}>
                  <tbody>
                    {balances
                      .filter((b) => b.qty_on_hand <= 0)
                      .slice(0, 5)
                      .map((b) => (
                        <ClickableRow key={`${b.warehouse_id}-${b.item_id}`} onClick={() => navigate('/app/stock-balance')}>
                          <td style={tdStyle}>{itemLabel(b.item_id)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--alex-danger)', fontWeight: 600 }}>{b.qty_on_hand}</td>
                        </ClickableRow>
                      ))}
                  </tbody>
                </table>
              )}
            </SectionCard>
          )}
        </div>
      </div>

      <PrDetailDrawer pr={selectedPr} open={!!selectedPr} onClose={() => setSelectedPr(null)} />
    </div>
  );
}
